import { db } from '../lib/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

// Database structure: vindie/{collection}

export interface ShareData {
  id: string;
  userId: string;
  contentId: string;
  contentType: string;
  shareMethod: 'link' | 'social' | 'embed';
  platform?: string;
  createdAt: Date;
}

export const shareContent = async (
  userId: string,
  contentId: string,
  contentType: string,
  shareMethod: 'link' | 'social' | 'embed',
  platform?: string
): Promise<void> => {
  try {
    const shareData: any = {
      userId,
      contentId,
      contentType,
      shareMethod,
      createdAt: new Date()
    };
    
    // Only include platform if it's defined
    if (platform) {
      shareData.platform = platform;
    }
    
    await addDoc(collection(db, 'shares'), shareData);
  } catch (error) {
    console.error('Error sharing content:', error);
    throw error;
  }
};

export const getShareStats = async (contentId: string): Promise<{
  totalShares: number;
  shareMethods: { [key: string]: number };
  recentShares: ShareData[];
}> => {
  try {
    const sharesQuery = query(
      collection(db, 'shares'),
      where('contentId', '==', contentId),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    
    const querySnapshot = await getDocs(sharesQuery);
    const shares: ShareData[] = [];
    const shareMethods: { [key: string]: number } = {};
    
    querySnapshot.forEach((doc) => {
      const shareData = { id: doc.id, ...doc.data() } as ShareData;
      shares.push(shareData);
      
      const method = shareData.shareMethod;
      shareMethods[method] = (shareMethods[method] || 0) + 1;
    });
    
    return {
      totalShares: shares.length,
      shareMethods,
      recentShares: shares.slice(0, 10)
    };
  } catch (error) {
    console.error('Error getting share stats:', error);
    throw error;
  }
};

export const generateShareLink = (contentId: string, contentType: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/${contentType}/${contentId}`;
};

export const shareToSocial = async (
  platform: 'twitter' | 'facebook' | 'linkedin' | 'reddit',
  contentId: string,
  contentType: string,
  title: string,
  description?: string
): Promise<void> => {
  const shareUrl = generateShareLink(contentId, contentType);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  // const encodedDescription = encodeURIComponent(description || '');
  
  let shareUrl_platform: string;
  
  switch (platform) {
    case 'twitter':
      shareUrl_platform = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
      break;
    case 'facebook':
      shareUrl_platform = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      break;
    case 'linkedin':
      shareUrl_platform = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      break;
    case 'reddit':
      shareUrl_platform = `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
      break;
    default:
      throw new Error('Unsupported platform');
  }
  
  window.open(shareUrl_platform, '_blank', 'width=600,height=400');
};
